import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { auditApi, AuditLogEntry } from "@/api/audit";
import { format } from "date-fns";
import { FileSearch, Loader2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ENTITY_TYPES = ["OPD_BILL", "PRESCRIPTION", "IPD_DISCHARGE", "LAB_RESULT", "PAYMENT_REFUND", "PHARMACY_ISSUE", "CASHIER_SESSION"];

export default function AuditLogPage() {
  const [entityType, setEntityType] = useState<string>("");
  const [entityId, setEntityId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [limit, setLimit] = useState(50);

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["audit-log", entityType, entityId, from, to, limit],
    queryFn: async () => {
      const res = await auditApi.getAuditLog({
        entityType: entityType || undefined,
        entityId: entityId || undefined,
        from: from || undefined,
        to: to || undefined,
        limit,
      });
      return (res.data?.data ?? []) as AuditLogEntry[];
    },
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSearch className="h-5 w-5" />
            Audit trail
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <div>
              <Label>Entity type</Label>
              <Select value={entityType || "all"} onValueChange={(v) => setEntityType(v === "all" ? "" : v)}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {ENTITY_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Entity ID</Label>
              <Input
                placeholder="Filter by ID"
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
                className="w-48"
              />
            </div>
            <div>
              <Label>From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" />
            </div>
            <div>
              <Label>Limit</Label>
              <Input type="number" min={10} max={200} value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10) || 50)} className="w-20" />
            </div>
          </div>
          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      No audit entries
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="whitespace-nowrap">{format(new Date(e.createdAt), "dd MMM yyyy HH:mm")}</TableCell>
                      <TableCell><span className="font-mono text-xs">{e.entityType}</span> / <span className="font-mono text-xs">{e.entityId.slice(0, 8)}…</span></TableCell>
                      <TableCell>{e.action}</TableCell>
                      <TableCell>{e.performedBy?.name ?? "-"}</TableCell>
                      <TableCell className="max-w-xs truncate">{e.metadata ? JSON.stringify(e.metadata) : "-"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
